/**
 * GM Soundfont Synth — multi-instrument sampler for MIDI audition.
 *
 * Sibling of {@link SalamanderPianoSynth} (see `./synth.ts`) but **does NOT**
 * implement the `Synth` interface: it takes `gmProgram` and `isDrum` per call.
 * Designed for the upload-modal "needs help" audition, NOT for the Studio.
 *
 * Design highlights (cf. `docs/spec/midi-quality-feedback.md` §5.2):
 *  - Tone.js dynamic import — required for Next.js SSR.
 *  - Lazy + dedup loading per GM program. A given program is loaded at most
 *    once per session; concurrent `ensureProgram(p)` calls share the same
 *    in-flight promise.
 *  - 15s load timeout. On timeout the loading promise resolves (no throw) so
 *    the caller never hangs; the sampler is marked unavailable and subsequent
 *    `noteOn` calls are silent no-ops.
 *  - `noteOn` / `noteOff` are silent no-ops when the target sampler is not
 *    ready: the UI is free to fire them without coordination.
 *  - Singleton: `getGmSoundfontSynth(ctx)` returns the same instance for the
 *    lifetime of the app. `dispose()` is reserved for app unload, NOT for
 *    modal-close (samplers persist across uploads).
 *
 * Sample source: MIDI.js soundfonts via GitHub Pages CDN.
 *   `${baseUrl}/${instrumentName}-mp3/${noteName}.mp3`
 *   `${baseUrl}/percussion-mp3/${noteName}.mp3` for drums.
 *
 * Pivot notes (`C3`, `C4`, `C5` for melodic instruments) are enough — Tone's
 * Sampler pitch-shifts between pivots. Drums use a wider list since drum
 * "pitches" map to distinct drum sounds, not transposed copies of one sample.
 */

import type { Sampler } from "tone";
import { Note } from "../music/note";
import { GM_DRUMS_NAME, GM_INSTRUMENT_NAMES } from "./gm-instruments";
import { ensureToneContext } from "./tone-context";

const DEFAULT_BASE_URL = "https://gleitz.github.io/midi-js-soundfonts/FluidR3_GM";

/** Pivot notes loaded for melodic instruments. Tone.Sampler pitch-shifts between them. */
const MELODIC_PIVOT_NOTES = ["C3", "C4", "C5"] as const;

/**
 * Drum pivot notes — GM drum map (channel 10) maps MIDI numbers to specific
 * drum hits (35 = Acoustic Bass Drum, 38 = Snare, 42 = Closed Hi-Hat, …).
 * MIDI.js exposes the standard 35..81 GM drum range. We load a representative
 * subset to cover the most common kits; missing notes get pitch-shifted from
 * the nearest sample by Tone.Sampler.
 */
const DRUM_PIVOT_NOTES = [
  "B1",  // 35 — Acoustic Bass Drum
  "C2",  // 36 — Bass Drum 1
  "D2",  // 38 — Acoustic Snare
  "E2",  // 40 — Electric Snare
  "F#2", // 42 — Closed Hi-Hat
  "A#2", // 46 — Open Hi-Hat
  "C#3", // 49 — Crash Cymbal 1
  "D#3", // 51 — Ride Cymbal 1
  "G3",  // 55 — Splash Cymbal
] as const;

const LOAD_TIMEOUT_MS = 15_000;

type ToneModule = typeof import("tone");

/** Composite key `${gmProgram}|${midi}` for active-note tracking. */
type ActiveKey = string;

function activeKey(midi: number, gmProgram: number, isDrum: boolean): ActiveKey {
  if (isDrum) {
    return `drum|${midi}`;
  }

  return `${gmProgram}|${midi}`;
}

function midiToNoteName(midi: number): string {
  // Sampler note names use sharps with octave, e.g. "C4", "F#3".
  return Note.fromMidi(midi).toName({ preferSharps: true, withOctave: true });
}

function buildPivotUrls(notes: readonly string[]): Record<string, string> {
  const urls: Record<string, string> = {};
  for (const note of notes) {
    urls[note] = `${note}.mp3`;
  }

  return urls;
}

/**
 * Map MIDI sharp note name to the MIDI.js URL form.
 * MIDI.js encodes sharps as plain `#` in the URL (e.g. `F#3.mp3`), but
 * the `#` MUST be percent-encoded for HTTP. We hand it to Tone.Sampler as
 * `Fs3.mp3` is NOT the convention here — MIDI.js / gleitz uses `F#3.mp3`
 * with the URL-safe percent encoding handled by the runtime. Tone.Sampler
 * builds the final URL via `baseUrl + url`; modern browsers encode the `#`
 * automatically in `fetch`, but to be safe we pre-encode it.
 */
function encodeSharp(noteFileName: string): string {
  return noteFileName.replace("#", "%23");
}

function buildEncodedPivotUrls(notes: readonly string[]): Record<string, string> {
  const urls: Record<string, string> = {};
  for (const note of notes) {
    urls[note] = encodeSharp(`${note}.mp3`);
  }

  return urls;
}

/**
 * Multi-instrument sampler. Lazy-loads GM programs on demand.
 *
 * Lifetime: singleton, persists for the whole app session. The upload modal
 * MUST NOT call `dispose()`; only the app unload should.
 */
export class GmSoundfontSynth {
  private readonly samplers = new Map<number, Sampler>();
  private drumSampler: Sampler | null = null;

  private readonly loadingPromises = new Map<number, Promise<void>>();
  private drumLoadingPromise: Promise<void> | null = null;

  private Tone: ToneModule | null = null;
  private tonePromise: Promise<ToneModule | null> | null = null;

  private readonly activeNotes = new Map<ActiveKey, { noteName: string; sampler: Sampler }>();

  private readonly baseUrl: string;

  constructor(private readonly ctx: AudioContext, opts?: { baseUrl?: string }) {
    this.baseUrl = (opts?.baseUrl ?? DEFAULT_BASE_URL).replace(/\/+$/, "");
  }

  /**
   * Lazy-loads a GM program. Idempotent — subsequent calls return the same
   * promise (or `Promise.resolve()` once loaded). Resolves even on failure
   * or timeout (the sampler simply stays unavailable for `noteOn`).
   */
  ensureProgram(gmProgram: number): Promise<void> {
    if (gmProgram < 0 || gmProgram > 127 || !Number.isInteger(gmProgram)) {
      return Promise.resolve();
    }

    if (this.samplers.has(gmProgram)) {
      return Promise.resolve();
    }

    const inflight = this.loadingPromises.get(gmProgram);
    if (inflight !== undefined) {
      return inflight;
    }

    const promise = this.loadProgram(gmProgram).finally(() => {
      this.loadingPromises.delete(gmProgram);
    });

    this.loadingPromises.set(gmProgram, promise);

    return promise;
  }

  /**
   * Lazy-loads the drum bank (channel 10). Idempotent.
   * Resolves even on failure or timeout.
   */
  ensureDrums(): Promise<void> {
    if (this.drumSampler !== null) {
      return Promise.resolve();
    }

    if (this.drumLoadingPromise !== null) {
      return this.drumLoadingPromise;
    }

    this.drumLoadingPromise = this.loadDrums().finally(() => {
      this.drumLoadingPromise = null;
    });

    return this.drumLoadingPromise;
  }

  /** True iff `gmProgram` is loaded and ready to receive `noteOn`. */
  isProgramReady(gmProgram: number): boolean {
    return this.samplers.has(gmProgram);
  }

  /** True iff the drum bank is loaded and ready. */
  isDrumsReady(): boolean {
    return this.drumSampler !== null;
  }

  /**
   * Trigger a note. Silent no-op if the target sampler is not yet loaded.
   *
   * @param midi MIDI note 0..127.
   * @param velocity Normalized 0..1.
   * @param at AudioContext time in seconds (cf. `ctx.currentTime`).
   *           `undefined` schedules immediately.
   * @param gmProgram GM program; ignored when `isDrum` is true.
   * @param isDrum True to route to the drum bank.
   */
  noteOn(
    midi: number,
    velocity: number,
    at: number | undefined,
    gmProgram: number,
    isDrum: boolean,
  ): void {
    const sampler = isDrum ? this.drumSampler : this.samplers.get(gmProgram) ?? null;
    if (sampler === null) {
      return;
    }

    const noteName = midiToNoteName(midi);
    const key = activeKey(midi, gmProgram, isDrum);

    // Release any previous instance of the same key on the same sampler
    // to avoid runaway voices on repeated note-on for the same pitch.
    const prev = this.activeNotes.get(key);
    if (prev !== undefined) {
      try {
        prev.sampler.triggerRelease(prev.noteName, at);
      } catch {
        // Ignore — sampler may have been disposed.
      }
    }

    const v = Math.max(0.01, Math.min(1, velocity));

    try {
      sampler.triggerAttack(noteName, at, v);
      this.activeNotes.set(key, { noteName, sampler });
    } catch {
      // Silent — invalid note or sampler not actually ready (race).
    }
  }

  /**
   * Release a previously triggered note. No-op if the matching attack
   * never fired (or already released).
   */
  noteOff(
    midi: number,
    at: number | undefined,
    gmProgram: number,
    isDrum: boolean,
  ): void {
    const key = activeKey(midi, gmProgram, isDrum);
    const active = this.activeNotes.get(key);
    if (active === undefined) {
      return;
    }

    try {
      active.sampler.triggerRelease(active.noteName, at);
    } catch {
      // Silent.
    }

    this.activeNotes.delete(key);
  }

  /**
   * Release every active note across all samplers. Cheap to call repeatedly;
   * used by the audition player on `stop()` and modal close.
   */
  releaseAllNotes(): void {
    for (const sampler of this.samplers.values()) {
      try {
        sampler.releaseAll();
      } catch {
        // Silent.
      }
    }

    if (this.drumSampler !== null) {
      try {
        this.drumSampler.releaseAll();
      } catch {
        // Silent.
      }
    }

    this.activeNotes.clear();
  }

  /**
   * Dispose of every loaded sampler. Reserved for app unload — the modal
   * MUST NOT call this; samplers persist across uploads.
   */
  dispose(): void {
    this.releaseAllNotes();

    for (const sampler of this.samplers.values()) {
      try {
        sampler.dispose();
      } catch {
        // Silent.
      }
    }

    this.samplers.clear();

    if (this.drumSampler !== null) {
      try {
        this.drumSampler.dispose();
      } catch {
        // Silent.
      }

      this.drumSampler = null;
    }

    this.loadingPromises.clear();
    this.drumLoadingPromise = null;
  }

  // ─── Internals ───────────────────────────────────────────────────

  private async ensureTone(): Promise<ToneModule | null> {
    if (this.Tone !== null) {
      return this.Tone;
    }

    if (this.tonePromise !== null) {
      return this.tonePromise;
    }

    if (typeof window === "undefined") {
      return null;
    }

    this.tonePromise = import("tone")
      .then((mod) => {
        this.Tone = mod;
        // Idempotent : le premier synth à charger Tone installe le contexte,
        // les suivants le retrouvent (cf. `lib/audio/tone-context.ts`).
        ensureToneContext(mod, this.ctx);

        return mod;
      })
      .catch((err: unknown) => {
        console.warn("GmSoundfontSynth: failed to load tone.js", err);

        return null;
      });

    return this.tonePromise;
  }

  private async loadProgram(gmProgram: number): Promise<void> {
    const Tone = await this.ensureTone();
    if (Tone === null) {
      return;
    }

    const name = GM_INSTRUMENT_NAMES[gmProgram];
    if (name === undefined) {
      return;
    }

    const baseUrl = `${this.baseUrl}/${name}-mp3/`;
    const urls = buildEncodedPivotUrls(MELODIC_PIVOT_NOTES);

    await this.createSamplerWithTimeout(
      Tone,
      urls,
      baseUrl,
      (sampler) => {
        this.samplers.set(gmProgram, sampler);
      },
      `program ${gmProgram} (${name})`,
    );
  }

  private async loadDrums(): Promise<void> {
    const Tone = await this.ensureTone();
    if (Tone === null) {
      return;
    }

    const baseUrl = `${this.baseUrl}/${GM_DRUMS_NAME}-mp3/`;
    const urls = buildEncodedPivotUrls(DRUM_PIVOT_NOTES);

    await this.createSamplerWithTimeout(
      Tone,
      urls,
      baseUrl,
      (sampler) => {
        this.drumSampler = sampler;
      },
      "drums",
    );
  }

  private createSamplerWithTimeout(
    Tone: ToneModule,
    urls: Record<string, string>,
    baseUrl: string,
    onReady: (sampler: Sampler) => void,
    label: string,
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      let settled = false;
      let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

      const settle = () => {
        if (settled) {
          return;
        }

        settled = true;
        if (timeoutHandle !== null) {
          clearTimeout(timeoutHandle);
          timeoutHandle = null;
        }

        resolve();
      };

      let sampler: Sampler;
      try {
        sampler = new Tone.Sampler({
          urls,
          baseUrl,
          attack: 0,
          release: 0.6,
          onload: () => {
            try {
              sampler.toDestination();
            } catch {
              // Silent — destination may not be available in test env.
            }

            onReady(sampler);
            settle();
          },
          onerror: (err: Error) => {
            console.warn(`GmSoundfontSynth: failed to load ${label}`, err);
            settle();
          },
        });
      } catch (err) {
        console.warn(`GmSoundfontSynth: Sampler construction failed for ${label}`, err);
        settle();

        return;
      }

      timeoutHandle = setTimeout(() => {
        console.warn(`GmSoundfontSynth: load timeout for ${label}`);
        settle();
      }, LOAD_TIMEOUT_MS);
    });
  }
}

// ─── Singleton ─────────────────────────────────────────────────────

let instance: GmSoundfontSynth | null = null;

/**
 * Returns the process-wide {@link GmSoundfontSynth} singleton.
 *
 * The first call instantiates the synth with the supplied `ctx`; subsequent
 * calls return the same instance regardless of the `ctx` argument (V1
 * assumes a single AudioContext shared by the Studio and the upload modal).
 *
 * For test isolation, see {@link __resetGmSoundfontSynthForTests}.
 */
export function getGmSoundfontSynth(ctx: AudioContext): GmSoundfontSynth {
  if (instance === null) {
    instance = new GmSoundfontSynth(ctx);
  }

  return instance;
}

/**
 * Test-only: drop the cached singleton so the next `getGmSoundfontSynth`
 * call rebuilds one. Never call from production code.
 *
 * @internal
 */
export function __resetGmSoundfontSynthForTests(): void {
  instance = null;
}
